import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from '../clsx';
import { Eyebrow } from './Eyebrow';
import styles from './Card.module.css';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'sunken' | 'dark' | undefined;
  /** 30/32 px padding as on auth and dialog cards. */
  padded?: boolean | undefined;
  /** No inner padding (tables, lists that draw their own rows). */
  flush?: boolean | undefined;
  /** `--shadow-1` instead of `--shadow-2` (board cards, tiles). */
  flat?: boolean | undefined;
  /** Hover lift for clickable tiles (`--shadow-3` + 1 px up). */
  interactive?: boolean | undefined;
}

export function Card({
  variant = 'default',
  padded = false,
  flush = false,
  flat = false,
  interactive = false,
  className,
  ...rest
}: CardProps) {
  return (
    <div
      className={clsx(
        styles.card,
        variant === 'sunken' && styles.sunken,
        variant === 'dark' && styles.dark,
        padded && styles.padded,
        flush && styles.flush,
        flat && styles.flat,
        interactive && styles.interactive,
        className,
      )}
      {...rest}
    />
  );
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  eyebrow?: ReactNode | undefined;
  title: ReactNode;
  subtitle?: ReactNode | undefined;
  /** Right-aligned controls (buttons, pills, links). */
  actions?: ReactNode | undefined;
  /** `lg` 20 px (card titles), `xl` 24 px (page-level cards such as the settings). */
  titleSize?: 'lg' | 'xl' | undefined;
}

/** Eyebrow · title (display font) · subtitle, with actions at the end of the row. */
export function CardHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  titleSize = 'lg',
  className,
  ...rest
}: CardHeaderProps) {
  return (
    <div className={clsx(styles.header, className)} {...rest}>
      <div className={styles.headerText}>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <div
          className={clsx(
            styles.title,
            titleSize === 'xl' && styles.titleXl,
            Boolean(eyebrow) && styles.titleAfterEyebrow,
          )}
        >
          {title}
        </div>
        {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
      </div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}
